/**
 * Maps Firebase Auth errors to user-friendly messages.
 * Ensures users never see raw Firebase error codes or the word "Firebase".
 */

export type AuthErrorContext = 'login' | 'signup' | 'reset_password' | 'employee_signup';

/** Firebase Auth error code → translation key (no "Firebase" or technical codes in UI) */
const AUTH_CODE_TO_KEY: Record<string, string> = {
  'auth/invalid-credential': 'auth_error_invalid_credential',
  'auth/invalid-email': 'auth_error_invalid_email',
  'auth/user-disabled': 'auth_error_user_disabled',
  'auth/user-not-found': 'auth_error_user_not_found',
  'auth/wrong-password': 'auth_error_wrong_password',
  'auth/email-already-in-use': 'auth_error_email_already_in_use',
  'auth/weak-password': 'auth_error_weak_password',
  'auth/too-many-requests': 'auth_error_too_many_requests',
  'auth/network-request-failed': 'auth_error_network',
  'auth/popup-closed-by-user': 'auth_error_popup_closed',
  'auth/cancelled-popup-request': 'auth_error_popup_closed',
  'auth/account-exists-with-different-credential': 'auth_error_account_exists_different_credential',
  'auth/operation-not-allowed': 'auth_error_operation_not_allowed',
  'auth/requires-recent-login': 'auth_error_requires_recent_login',
  'auth/credential-already-in-use': 'auth_error_credential_already_in_use',
  'auth/expired-action-code': 'auth_error_expired_code',
  'auth/invalid-action-code': 'auth_error_invalid_code',
};

/**
 * Returns a user-friendly error message for auth errors, or null if the error
 * should not be shown (e.g. user closed popup).
 * Never returns a string containing "Firebase" or raw auth/ codes.
 */
export function getAuthErrorMessage(
  error: unknown,
  t: (key: string) => string,
  context: AuthErrorContext
): string | null {
  if (error == null) return t('auth_error_generic');

  const code =
    typeof (error as { code?: string }).code === 'string'
      ? (error as { code: string }).code
      : '';
  const message = error instanceof Error ? error.message : String(error);

  // Popup closed by user – don't show an error
  if (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    message.includes('popup-closed-by-user') ||
    message.toLowerCase().includes('cancelled')
  ) {
    return null;
  }

  // Prefer code-based mapping
  const translationKey = AUTH_CODE_TO_KEY[code];
  if (translationKey) {
    const translated = t(translationKey);
    if (translated && translated !== translationKey) return translated;
  }

  // Fallback: check message for known auth/ codes (Firebase sometimes only puts it in message)
  for (const [authCode, key] of Object.entries(AUTH_CODE_TO_KEY)) {
    if (message.includes(authCode)) {
      const translated = t(key);
      if (translated && translated !== key) return translated;
    }
  }

  // Never expose raw message if it contains Firebase or auth/
  if (message.includes('Firebase') || message.includes('auth/')) {
    return t('auth_error_generic');
  }

  // Non-Firebase error (e.g. network): use generic or a safe substring
  return t('auth_error_generic');
}
